import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ReactReader } from 'react-reader';
import { X, BookOpen as BookOpenIcon, Lock, Loader2, ChevronLeft, ChevronRight, Sun, Moon, ShieldCheck } from 'lucide-react';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import { useReadingTracker } from '../hooks/useReadingTracker';
import axios from 'axios';

const getAssetBaseUrl = () => window.location.origin;
const API = `${getAssetBaseUrl()}/api`;

const EpubReader = ({ bookUrl, onClose, bookId, totalPages = 100 }) => {
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalLocations, setTotalLocations] = useState(0);
  const [readingMode, setReadingMode] = useState(() => localStorage.getItem('readingMode') || 'dark');
  const [toc, setToc] = useState([]);
  const renditionRef = useRef(null);
  const tocRef = useRef([]);
  const { toast } = useToast();
  const token = localStorage.getItem('token');
  const baseUrl = getAssetBaseUrl();

  // AI Verified Progress
  const [verifiedProgress, setVerifiedProgress] = useState(null);
  const canComplete = verifiedProgress?.can_complete || false;
  const verifiedPct = verifiedProgress?.verified_percentage || 0;

  const { setPageInfo, consumeViewCredit, flushEvents, fetchVerifiedProgress } = useReadingTracker({
    bookId,
    token,
    enabled: !!bookId && !!token,
    onProgressUpdate: (data) => setVerifiedProgress(data)
  });

  // Build full URL
  const fullUrl = bookUrl?.startsWith('/api')
    ? `${baseUrl}${bookUrl}?token=${token}`
    : bookUrl?.startsWith('http')
    ? bookUrl
    : `${baseUrl}${bookUrl}`;

  // Consume view credit on open
  useEffect(() => {
    if (bookId && token) {
      consumeViewCredit();
    }
  }, [bookId, token, consumeViewCredit]);

  // Load saved position
  useEffect(() => {
    const loadPosition = async () => {
      if (!bookId || !token) return;
      try {
        const resp = await axios.get(`${API}/library/position/${bookId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (resp.data?.epub_cfi) {
          setLocation(resp.data.epub_cfi);
        }
      } catch {
        // No saved position
      }
    };
    loadPosition();
  }, [bookId, token]);

  // Save position periodically
  const savePosition = useCallback(async (epubcfi, pageNum) => {
    if (!bookId || !token || !epubcfi) return;
    try {
      await axios.post(`${API}/library/position/${bookId}`, {
        page_number: pageNum || 1,
        scroll_position: 0,
        epub_cfi: epubcfi,
        total_pages: totalLocations || totalPages
      }, { headers: { Authorization: `Bearer ${token}` } });
    } catch {
      // silent
    }
  }, [bookId, token, totalLocations, totalPages]);

  // Track progress
  const trackProgress = useCallback(async (percentage) => {
    if (!bookId || !token) return;
    try {
      await axios.post(`${API}/books/${bookId}/progress`, {
        progress_percentage: percentage
      }, { headers: { Authorization: `Bearer ${token}` } });
    } catch {
      // silent
    }
  }, [bookId, token]);

  const locationChanged = (epubcfi) => {
    setLocation(epubcfi);
    if (renditionRef.current && totalLocations > 0) {
      const currentLoc = renditionRef.current.book?.locations?.currentLocation;
      if (currentLoc) {
        const page = renditionRef.current.book.locations.locationFromCfi(epubcfi);
        if (page !== undefined) {
          const pageNum = page + 1;
          setCurrentPage(pageNum);
          const pct = Math.max(1, Math.min(100, Math.round((pageNum / totalLocations) * 100)));
          trackProgress(pct);
          setPageInfo(pageNum, totalLocations);
          // Save position every location change
          savePosition(epubcfi, pageNum);
        }
      }
    }
  };

  const getReaderStyles = () => {
    if (readingMode === 'light') {
      return { body: { background: '#ffffff', color: '#1a1a1a' }, p: { color: '#1a1a1a' } };
    }
    if (readingMode === 'sepia') {
      return { body: { background: '#f5e6c8', color: '#5b4636' }, p: { color: '#5b4636' } };
    }
    return { body: { background: '#1a1a2e', color: '#e0e0e0' }, p: { color: '#e0e0e0' } };
  };

  // Cleanup
  useEffect(() => {
    return () => {
      flushEvents();
    };
  }, [flushEvents]);

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col" data-testid="epub-reader">
      {/* Header */}
      <div className="bg-gray-900 border-b border-gray-700 p-3 sm:p-4 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center space-x-2 sm:space-x-4">
          <Button variant="ghost" size="icon" onClick={onClose} className="text-white h-8 w-8 sm:h-10 sm:w-10">
            <X className="h-5 w-5 sm:h-6 sm:w-6" />
          </Button>
          <div className="flex items-center space-x-2">
            <BookOpenIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            <span className="text-white font-semibold text-sm sm:text-base">Reading Book</span>
            {currentPage > 0 && totalLocations > 0 && (
              <span className="text-gray-400 text-xs sm:text-sm">
                {currentPage} / {totalLocations}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center space-x-1 sm:space-x-3">
          {verifiedProgress && (
            <div className="hidden sm:flex items-center gap-2 mr-2" data-testid="epub-verified-progress">
              <ShieldCheck className={`h-4 w-4 ${canComplete ? 'text-green-400' : 'text-amber-400'}`} />
              <div className="flex flex-col">
                <div className="flex items-center gap-1.5">
                  <div className="w-20 h-1.5 bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-700 ${canComplete ? 'bg-green-500' : 'bg-amber-500'}`}
                      style={{ width: `${Math.min(verifiedPct, 100)}%` }}
                    />
                  </div>
                  <span className={`text-[10px] font-bold ${canComplete ? 'text-green-400' : 'text-amber-400'}`}>
                    {verifiedPct}%
                  </span>
                </div>
              </div>
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              const newMode = readingMode === 'dark' ? 'light' : readingMode === 'light' ? 'sepia' : 'dark';
              setReadingMode(newMode);
              localStorage.setItem('readingMode', newMode);
              // Apply theme to rendition
              if (renditionRef.current) {
                const styles = readingMode === 'dark'
                  ? { body: { background: '#ffffff', color: '#1a1a1a' } }
                  : readingMode === 'light'
                  ? { body: { background: '#f5e6c8', color: '#5b4636' } }
                  : { body: { background: '#1a1a2e', color: '#e0e0e0' } };
                renditionRef.current.themes.override('color', styles.body.color);
                renditionRef.current.themes.override('background', styles.body.background);
              }
            }}
            className={`text-xs sm:text-sm px-2 sm:px-3 ${
              readingMode === 'light' ? 'text-yellow-400' :
              readingMode === 'sepia' ? 'text-amber-400' :
              'text-blue-400'
            }`}
            data-testid="epub-reading-mode-toggle"
          >
            {readingMode === 'dark' ? <Moon className="h-4 w-4 sm:mr-2" /> : <Sun className="h-4 w-4 sm:mr-2" />}
            <span className="hidden sm:inline capitalize">{readingMode}</span>
          </Button>
          <div className="hidden sm:flex items-center space-x-1 text-gray-400 text-xs ml-2">
            <Lock className="h-3 w-3" />
            <span>Protected</span>
          </div>
        </div>
      </div>

      {/* EPUB Reader */}
      <div className={`flex-1 relative ${
        readingMode === 'light' ? 'bg-white' : readingMode === 'sepia' ? 'bg-amber-50' : 'bg-gray-900'
      }`} style={{ minHeight: 0 }}>
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-800 z-10">
            <div className="text-center text-white">
              <Loader2 className="h-12 w-12 animate-spin text-red-500 mx-auto mb-4" />
              <p className="font-medium">Loading book...</p>
            </div>
          </div>
        )}

        {error ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-white p-4">
              <p className="text-xl mb-4">Unable to load book</p>
              <p className="text-gray-400 mb-6">The EPUB file could not be loaded. Please try again.</p>
              <Button onClick={onClose} className="bg-red-600 hover:bg-red-700">
                Close
              </Button>
            </div>
          </div>
        ) : (
          <div style={{ height: '100%', position: 'relative' }}>
            <ReactReader
              url={fullUrl}
              location={location}
              locationChanged={locationChanged}
              showToc={false}
              epubInitOptions={{
                openAs: 'epub'
              }}
              getRendition={(rendition) => {
                renditionRef.current = rendition;
                setLoading(false);

                // Apply initial theme
                const styles = getReaderStyles();
                rendition.themes.default({
                  body: styles.body,
                  'p, span, div, h1, h2, h3, h4, h5, h6, li, a': { color: `${styles.body.color} !important` }
                });

                // Generate locations for page tracking
                rendition.book.ready.then(() => {
                  return rendition.book.locations.generate(1024);
                }).then(() => {
                  setTotalLocations(rendition.book.locations.length());
                });
              }}
              tocChanged={(t) => {
                tocRef.current = t;
                setToc(t);
              }}
              epubOptions={{
                flow: 'paginated',
                manager: 'default'
              }}
              readerStyles={{
                ...ReactReaderStyle,
                container: {
                  ...ReactReaderStyle.container,
                  background: readingMode === 'light' ? '#ffffff' : readingMode === 'sepia' ? '#f5e6c8' : '#1a1a2e'
                },
                readerArea: {
                  ...ReactReaderStyle.readerArea,
                  background: readingMode === 'light' ? '#ffffff' : readingMode === 'sepia' ? '#f5e6c8' : '#1a1a2e'
                },
                arrow: {
                  ...ReactReaderStyle.arrow,
                  color: readingMode === 'dark' ? '#888' : '#555'
                }
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// Default react-reader styles (override for theming)
const ReactReaderStyle = {
  container: { overflow: 'hidden', position: 'relative', height: '100%' },
  readerArea: { position: 'relative', zIndex: 1, height: '100%', width: '100%', background: '#1a1a2e' },
  containerExpanded: { transform: 'translateX(256px)' },
  titleArea: { position: 'absolute', top: 20, left: 50, right: 50, textAlign: 'center', color: '#999' },
  reader: { position: 'absolute', top: 50, left: 50, bottom: 20, right: 50 },
  swipeWrapper: { position: 'absolute', top: 0, left: 0, bottom: 0, right: 0, zIndex: 200 },
  prev: { left: 1, cursor: 'pointer' },
  next: { right: 1, cursor: 'pointer' },
  arrow: { outline: 'none', border: 'none', background: 'none', position: 'absolute', top: '50%', marginTop: -32, fontSize: 64, padding: '0 10px', color: '#888', fontFamily: 'arial, sans-serif', cursor: 'pointer', userSelect: 'none', appearance: 'none', fontWeight: 'normal' },
  arrowHover: { color: '#ccc' },
  tocBackground: { position: 'absolute', left: 256, right: 0, top: 0, bottom: 0, zIndex: 1 },
  tocArea: { position: 'absolute', left: 0, top: 0, bottom: 0, zIndex: 0, width: 256, overflowY: 'auto', WebkitOverflowScrolling: 'touch', background: '#1a1a2e', padding: '10px 0' },
  tocAreaButton: { userSelect: 'none', appearance: 'none', background: 'none', border: 'none', display: 'block', fontFamily: 'sans-serif', width: '100%', fontSize: '.9em', textAlign: 'left', padding: '.9em 1em', color: '#ccc', boxSizing: 'border-box', outline: 'none', cursor: 'pointer' },
  tocButtonExpanded: { background: '#333' },
  tocButtonBar: { position: 'absolute', top: '10px', left: '10px', zIndex: 2 },
  tocButton: { background: 'none', border: 'none', width: 32, height: 32, position: 'relative', padding: 0, cursor: 'pointer', outline: 'none' }
};

export default EpubReader;
